import { useMemo, useState } from 'react'
import { useEditor } from '@/state/store'
import { buildShareLink } from '@/lib/share'

/**
 * Share dialog (Track 7a): turns the current design into an anonymous, read-only
 * URL (the whole project is LZ-compressed into the `#d=` hash — no backend). Big
 * designs that won't fit a URL show a friendly cap message instead of a broken link.
 */
export function ShareModal({ onClose }: { onClose: () => void }) {
  const project = useEditor((s) => s.project)
  const link = useMemo(() => buildShareLink(project), [project])
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(link.url)
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch {
      // Clipboard blocked — the field is selectable as a fallback.
      setCopied(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" role="dialog" aria-label="Share design" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Share this design</h3>
          <button className="ghost-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {link.tooLarge ? (
          <p className="modal-warn">
            This design is too large for a link — cloud sharing (coming with Drive) will handle
            big designs. For now, try sharing a smaller layout.
          </p>
        ) : (
          <>
            <div className="share-row">
              <input className="txt share-url" readOnly value={link.url} onFocus={(e) => e.currentTarget.select()} />
              <button className="primary-btn" onClick={copy}>{copied ? 'Copied ✓' : 'Copy'}</button>
            </div>
            <p className="hint-note">
              Anyone with this link can <b>view</b> (read-only). They can <b>fork</b> their own
              editable copy.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
